import { localDateAt, type ScheduleEntry } from "@edu/shared";
import { mondayOf } from "./common.ts";

/**
 * Адрес расписания, раскрывающий карточку конкретного занятия.
 *
 * Зачем модуль. Выход из комнаты урока это ровно та минута, в которую делаются
 * отметка о приходе, оплата и домашка, а живут все три в карточке занятия.
 * Раньше выход уводил на корень, оттуда на расписание текущей недели, и человек
 * заново искал коробку занятия глазами в сетке. Теперь комната зовёт расписание
 * по имени занятия, и три работы начинаются с уже раскрытой карточки.
 *
 * Четыре решения, и каждое закрывает свою поломку.
 *
 * 1. **Занятие названо в АДРЕСЕ, а не передано состоянием.** Так работает
 *    «назад» браузера и так ссылку можно переслать: адрес это единственное
 *    место, которое переживает и перезагрузку вкладки, и пересылку в переписку.
 *    Тем же правилом в адресе живут неделя, режим и день (`SchedulePage`)
 * 2. **Момент занятия едет числом (`at`), а местная дата считается на
 *    расписании.** Считать её здесь значило бы завести вторую копию правила
 *    «всё в зоне СМОТРЯЩЕГО», причём в комнате урока, которая про зоны не знает
 *    ничего. Плюс пересланная ссылка тогда несла бы дату отправителя, и у
 *    получателя по другую сторону полуночи открывалась бы соседняя неделя
 * 3. **Урок сопоставляется занятию ЗДЕСЬ, а не вторым запросом.** Строка сетки
 *    уже несёт `lessonId`, и лишний круг по сети на канале 128 кбит/с человек
 *    видит как подвисание ровно в момент выхода с урока
 * 4. **Не нашлось это штатный случай, а не отказ.** Урок без времени, чужой,
 *    осиротевший, удалённый: расписание открывается обычным, без карточки и без
 *    красной строки. Сказать «занятие не найдено» тому, кто просто вышел с
 *    урока, значит объявить поломкой то, что поломкой не является
 */

/** Имена параметров адреса. Их пишет комната, а читает расписание. */
export const P_LESSON = "lesson";
export const P_AT = "at";

/**
 * Имена параметров окна, которые расписание ведёт и без всякой ссылки.
 *
 * ⚠️ Объявлены здесь, а не в `SchedulePage`, ровно потому, что ссылка обязана
 * уступать им своё место при уборке: два списка имён разъехались бы молча, и
 * неделя занятия перестала бы записываться.
 */
export const P_WEEK = "week";
export const P_DAY = "day";

export interface LessonLink {
  lessonId: string;
  /** Момент занятия по слепку урока. `null` у урока без времени. */
  at: number | null;
  /**
   * Местная дата занятия в зоне СМОТРЯЩЕГО: из неё выводятся и неделя, и день
   * узких режимов. `null`, когда момент не приехал: тогда показывается обычная
   * неделя, и занятие найдётся, только если оно в ней.
   */
  day: string | null;
}

/**
 * Адрес, которым комната урока возвращает человека в расписание.
 *
 * Момент кладётся слепком урока (`scheduledAt`), а не текущим временем: у
 * прошедшего занятия это его собственный момент, и именно он называет неделю.
 */
export function scheduleLessonHref(lessonId: string, at: number | null | undefined): string {
  const p = new URLSearchParams({ [P_LESSON]: lessonId });
  if (typeof at === "number") p.set(P_AT, String(at));
  return `/schedule?${p.toString()}`;
}

/**
 * Разбор адреса.
 *
 * ⚠️ Мусор в моменте не отменяет всей ссылки: занятие названо уроком, а момент
 * это подсказка про неделю. Ссылка, у которой хвост побился при пересылке,
 * обязана открыть расписание, а не пустоту.
 */
export function parseLessonLink(
  lessonParam: string | null,
  atParam: string | null,
  zone: string,
): LessonLink | null {
  if (lessonParam === null || lessonParam === "") return null;
  const at = atParam === null ? Number.NaN : Number(atParam);
  const ok = Number.isSafeInteger(at) && at > 0;
  return { lessonId: lessonParam, at: ok ? at : null, day: ok ? localDateAt(at, zone) : null };
}

/**
 * Строка сетки, за которой стоит этот урок.
 *
 * Два правила, и второе не запасное, а единственно возможное для группы.
 *
 * 1. **По `lessonId`.** Так находятся все занятия пары 1:1 (и у преподавателя, и
 *    у ученика), разовые занятия, осиротевшие уроки и уже разобранное занятие
 *    группы
 * 2. **По моменту, и только у занятия ГРУППЫ.** У преподавателя занятие группы
 *    приезжает одной сводной строкой на весь пучок, и урок у неё появляется
 *    вместе с разбором, а до него там `null` (`collapseBunches` в
 *    `apps/api/src/schedule-group-bunch.ts`). То есть преподаватель, вышедший из
 *    живой групповой комнаты, по первому правилу не нашёл бы своё занятие
 *    никогда. Момент у сводной строки и у урока один и тот же: урок сеанса
 *    заводится ровно на разрешённый момент занятия
 *
 * ⚠️ Второе правило намеренно НЕ распространяется на занятия 1:1: там первое
 * правило и так отвечает, а время без имени участника у преподавателя с двумя
 * занятиями внахлёст раскрыло бы карточку чужого занятия, и человек отметил бы
 * приход не тому. По той же причине неоднозначность (два занятия группы в один
 * момент) это отказ искать, а не выбор первого попавшегося.
 *
 * ⚠️ Два предиката во втором правиле сужают его до единственной строки, ради
 * которой оно и заведено, и без каждого из них правило раскрывает НЕ ТО:
 *
 * - **`lessonId === null`.** Строка с чужим уроком в это правило попадать не
 *   должна вовсе: наш урок уже проверен первым правилом, а у сводной строки урок
 *   появляется вместе с разбором и принадлежит другому сеансу. Одна совпавшая
 *   строка от этого не спасает: она отсекает неоднозначность СРЕДИ совпавших, а
 *   не случай, когда нужная строка не совпала, а чужая совпала
 * - **`state !== "moved_away"`.** Заглушка перенесённого занятия стоит на СТАРОМ
 *   месте, несёт исходный момент и урока не носит намеренно (`schedule-grid.ts`,
 *   «урок цепляется к занятию на его НОВОМ месте»), то есть проходит фильтр
 *   целиком. Раскрылась бы карточка «перенесено на четверг», в которой нет ни
 *   отметки о приходе, ни оплаты, ни домашки, то есть ровно тех трёх работ, ради
 *   которых сюда и возвращают
 */
export function findEntryOfLesson(
  entries: readonly ScheduleEntry[],
  lessonId: string,
  at: number | null,
): ScheduleEntry | null {
  const byLesson = entries.find((e) => e.lessonId === lessonId);
  if (byLesson !== undefined) return byLesson;
  if (at === null) return null;
  const groups = entries.filter(
    (e) => e.kind === "group" && e.at === at && e.lessonId === null && e.state !== "moved_away",
  );
  return groups.length === 1 ? (groups[0] ?? null) : null;
}

/**
 * Покрывает ли приехавшая неделя день занятия из ссылки.
 *
 * ⚠️ Границы берутся у САМОГО ответа (`from` и `to` в `ScheduleResponse`), а не
 * у экрана, и это не педантизм. Ссылку могут открыть во вкладке, где расписание
 * уже показано: тогда экран уже перевёл окно на неделю занятия, а в памяти ещё
 * лежит прошлый ответ, и по границам экрана он читался бы как нужный. Раскрытие
 * сгорело бы на чужой неделе, второй попытки нет.
 *
 * Ссылка без момента покрывается любой неделей: другого указания, где искать,
 * у нас нет, и единственный разумный ответ это «ищи в том, что открыто».
 */
export function weekCovers(from: string, to: string, day: string | null): boolean {
  return day === null || (day >= from && day <= to);
}

/**
 * Адрес без имени занятия: работа сделана, и ссылка обязана уйти из истории.
 *
 * ⚠️ Уйти в пустоту она не имеет права: неделя и день занятия встают на её
 * место обычными параметрами. Просто вычеркнуть `?lesson=` значило бы вернуть
 * экран на текущую неделю, то есть увести человека с занятия, ради которого его
 * сюда и привели. `null` в дне это «не двигать окно», и он законен ровно тогда,
 * когда двигать его было нечем: момент не приехал.
 */
export function addressWithoutLink(params: URLSearchParams, day: string | null): URLSearchParams {
  const p = new URLSearchParams(params);
  p.delete(P_LESSON);
  p.delete(P_AT);
  if (day !== null) {
    p.set(P_WEEK, mondayOf(day));
    p.set(P_DAY, day);
  }
  return p;
}
